import { NgModule } from '@angular/core'
import type { ExtraOptions, Routes } from '@angular/router'
import { RouterModule } from '@angular/router'

import { AuthLayoutComponent, MainLayoutComponent } from './app.component'
import { isAuthenticatedGuard } from './guards/is-authenticated.guard'
import { isDemoEnabled } from './guards/is-demo-enabled'
import { languageRouteGuard } from './guards/language-route.guard'
import { oldArticleIdRedirectGuard } from './guards/old-articleid.guard'
import { CreateOrganizationComponent } from './pages/organization-create/create-organization.component'

// The entire app, mounted once for Dutch (unprefixed) and once more under
// /en. Built by a factory (rather than shared as one array) so each locale
// branch gets its own Route objects - Angular caches lazy-loaded module
// injectors on the Route object itself, so reusing the same instances for
// both branches would make them share loaded child injectors.
function createCoreRoutes(): Routes {
  return [
    {
      path: 'auth',
      component: AuthLayoutComponent,
      loadChildren: () =>
        import('./features/auth/auth.module').then(m => m.AuthModule),
    },
    {
      path: 'demo',
      component: MainLayoutComponent,
      canMatch: [isDemoEnabled],
      loadChildren: () =>
        import('./features/demo.module').then(m => m.DemoModule),
    },
    {
      path: '',
      component: MainLayoutComponent,
      children: [
        {
          path: 'organization/create',
          canActivate: [isAuthenticatedGuard],
          component: CreateOrganizationComponent,
        },
        {
          path: 'organization',
          canActivate: [isAuthenticatedGuard],
          loadChildren: () =>
            import('./pages/organization/organization.component').then(
              m => m.organizationRoutes
            ),
        },
        {
          path: 'account',
          canActivate: [isAuthenticatedGuard],
          loadChildren: () =>
            import('./pages/account/account.component').then(
              m => m.accountRoutes
            ),
        },
        {
          path: '',
          children: [
            {
              path: '',
              loadChildren: () =>
                import('./pages/home/home.component').then(m => m.HomeRoutes),
            },
            {
              path: 'magazines',
              loadChildren: () =>
                import('./features/magazines-and-journals/magazines-and-journal.module').then(
                  m => m.MagazinesAndJournalModule
                ),
            },
            {
              path: 'editions',
              loadChildren: () =>
                import('./features//editions/editions.module').then(
                  m => m.EditionsModule
                ),
            },
            {
              path: 'authors',
              loadChildren: () =>
                import('./features//authors/authors.module').then(
                  m => m.AuthorsModule
                ),
            },
            {
              path: 'editors',
              loadChildren: () =>
                import('./features//editors/editors.module').then(
                  m => m.EditorsModule
                ),
            },
            {
              path: 'articles',
              loadChildren: () =>
                import('./features//articles/articles.module').then(
                  m => m.ArticlesModule
                ),
            },
            {
              path: 'pricing',
              redirectTo: 'products',
            },
            {
              path: 'products',
              loadChildren: () =>
                import('./features//products/products.module').then(
                  m => m.ProductsModule
                ),
            },
            {
              path: 'books-and-contracts',
              loadChildren: () =>
                import('./features//books-and-contracts/books-and-contracts.module').then(
                  m => m.BooksAndContractsModule
                ),
            },
            {
              path: '',
              loadChildren: () =>
                import('./features/information/information.module').then(
                  m => m.InformationModule
                ),
            },
            {
              path: '',
              loadChildren: () =>
                import('./features/old-site/old-site.module').then(
                  m => m.OldSiteModule
                ),
            },
            {
              path: '',
              loadChildren: () =>
                import('./pages/page/page.component').then(m => m.PageRoutes),
            },
            { path: '**', redirectTo: '', pathMatch: 'full' },
          ],
        },
      ],
      canActivate: [oldArticleIdRedirectGuard],
    },
  ]
}

const routes: Routes = [
  // 'en' must come before the pathless nl branch: nl's own catch-all
  // wildcard (nested inside createCoreRoutes()) would otherwise swallow
  // every /en/* URL before the router ever tries this sibling.
  {
    path: 'en',
    children: createCoreRoutes(),
    canActivate: [languageRouteGuard('en')],
  },
  {
    path: '',
    children: createCoreRoutes(),
    canActivate: [languageRouteGuard('nl')],
  },
]

const routerOptions: ExtraOptions = {
  anchorScrolling: 'enabled',
  scrollPositionRestoration: 'enabled',
  enableTracing: false,
}

@NgModule({
  imports: [RouterModule.forRoot(routes, routerOptions)],
  exports: [RouterModule],
})
export class AppRoutingModule {}
